// 결제 제안 띠(docs/monetization.md §6): 특정 순간(레벨 10·20·30·40, 전직 완료, 같은 스테이지 2연속 패배) 뒤 마을에서 화면 위쪽 가장자리에 얇은 띠 하나.
// 플레이 영역을 가리지 않는다(마을에서만, 창이 열려 있으면 미룸). 카운트다운·"곧 사라짐" 같은 압박 문구 없음.
// 빈도: 한국 날짜당 1회 + 같은 순간 7일에 1회. 표시 기록은 기기 전용 localStorage 키(dungeonhero.offers.v1, 진행 키가 아니라 동기화하지 않음 — account/keys.ts LOCAL_ONLY_KEYS).
// 게임 로직은 건드리지 않는다: 이 파일이 이벤트 버퍼(view.events)를 직접 읽는다. 순수 판정(쿨다운·상품 선택·연속 패배)은 DOM 없이 시험한다(offers.test.ts).
import { Ev, type GameEvent } from '../shared/events';
import { Scene, type GameView } from '../shared/types';
import { C, FONT, el } from '../ui/dom';
import { MENU_EVENT } from '../ui/menuBar';
import { OPEN_IAP_TAB_EVENT } from '../ui/uiEvents';
import { iapClient } from './client';
import { productExtras, type IapProductView } from './model';

export type OfferTrigger = 'levelup' | 'job' | 'defeat';
export const OFFER_KEY = 'dungeonhero.offers.v1';
export const OFFER_LEVELS: readonly number[] = [10, 20, 30, 40];
export const OFFER_TRIGGER_COOLDOWN_MS = 7 * 86_400_000;
const KST_OFFSET_MS = 9 * 3_600_000;

export interface OfferState { day: string; last: Partial<Record<OfferTrigger, number>> }
export const emptyOfferState = (): OfferState => ({ day: '', last: {} });

/** 한국 날짜(YYYY-MM-DD) */
export function kstDayKey(now: number): string { return new Date(now + KST_OFFSET_MS).toISOString().slice(0, 10); }

export function parseOfferState(raw: string | null): OfferState {
  if (!raw) return emptyOfferState();
  try {
    const o = JSON.parse(raw) as { day?: unknown; last?: unknown } | null;
    const last: Partial<Record<OfferTrigger, number>> = {};
    if (o && typeof o.last === 'object' && o.last) {
      for (const k of ['levelup', 'job', 'defeat'] as const) {
        const v = (o.last as Record<string, unknown>)[k];
        if (typeof v === 'number' && Number.isFinite(v)) last[k] = v;
      }
    }
    return { day: typeof o?.day === 'string' ? o.day : '', last };
  } catch { return emptyOfferState(); }
}

/** 지금 이 순간 제안해도 되는가: 오늘(한국) 이미 보였으면 안 되고, 같은 순간은 7일에 1회 */
export function canOffer(st: OfferState, trigger: OfferTrigger, now: number): boolean {
  if (st.day === kstDayKey(now)) return false;
  const last = st.last[trigger];
  return last === undefined || now - last >= OFFER_TRIGGER_COOLDOWN_MS || now < last;
}
export function markOffered(st: OfferState, trigger: OfferTrigger, now: number): OfferState {
  return { day: kstDayKey(now), last: { ...st.last, [trigger]: now } };
}

/** 레벨업 이벤트가 마일스톤(10·20·30·40)을 지나갔는가(한 번에 여러 레벨이면 건너뛴 칸도 센다) */
export function crossedMilestone(level: number, steps: number): number | null {
  const from = level - Math.max(1, steps);
  for (const m of OFFER_LEVELS) if (m > from && m <= level) return m;
  return null;
}

/** 같은 스테이지 2연속 패배 판정(다른 스테이지 패배는 1부터 다시, 클리어(결과)는 리셋) */
export class DefeatStreak {
  private stage = '';
  private n = 0;
  /** 패배를 기록하고, 이번이 같은 스테이지 2연속이면 true */
  defeat(stageId: string): boolean {
    if (!stageId) { this.reset(); return false; }
    if (stageId === this.stage) this.n++; else { this.stage = stageId; this.n = 1; }
    return this.n === 2;
  }
  reset(): void { this.stage = ''; this.n = 0; }
}

/** 제안할 상품: 스타터 팩(안 샀고 이 채널에서 팔 때) → 아니면 월정액(monthly30). 둘 다 없으면 null */
export function pickOfferProduct(products: readonly IapProductView[], bought: ReadonlySet<string>, play: boolean): IapProductView | null {
  const ok = (p: IapProductView | undefined): p is IapProductView => !!p && p.enabled && (!play || !!p.playSku);
  const starter = products.find((p) => p.id === 'starter_pack');
  if (ok(starter) && !productExtras(starter, bought.has(starter.id)).owned) return starter;
  const monthly = products.find((p) => p.id === 'monthly30');
  return ok(monthly) ? monthly : null;
}

export function offerText(trigger: OfferTrigger, p: IapProductView): string {
  const lead = trigger === 'levelup' ? '레벨이 올랐어요.' : trigger === 'job' ? '전직을 축하해요.' : '준비를 갖추면 도움이 될 수 있어요.';
  return p.id === 'starter_pack' ? `${lead} 시작 지원 꾸러미(계정당 1회)가 있어요.` : `${lead} 월정액(30일, 자동 갱신 없음)이 있어요.`;
}

function loadState(): OfferState { try { return parseOfferState(localStorage.getItem(OFFER_KEY)); } catch { return emptyOfferState(); } }
function saveState(st: OfferState): void { try { localStorage.setItem(OFFER_KEY, JSON.stringify(st)); } catch { /* 기기 표시만 못 남김 — 이번 세션은 shownDay로 막는다 */ } }

/**
 * 제안 띠. ui/index.ts가 만들고 매 프레임 update()를 부른다.
 * 순간이 오면 대기(pending)에 올리고, 마을이고 창이 닫혀 있을 때 쿨다운을 확인해 띠를 보인다.
 */
export class OfferBand {
  private readonly root: HTMLDivElement;
  private readonly text: HTMLSpanElement;
  private cursor: number;
  private pending: OfferTrigger | null = null;
  private shownTrigger: OfferTrigger | null = null;
  private shownProduct = '';
  private sessionDay = '';
  private busy = false;
  private readonly streak = new DefeatStreak();

  constructor(parent: HTMLElement, private readonly view: GameView) {
    this.cursor = view.events.written;
    const r = el('div', 'position:absolute;left:50%;top:4px;transform:translateX(-50%);display:none;align-items:center;gap:8px;box-sizing:border-box;max-width:min(560px,calc(100% - 24px));' +
      'min-height:38px;padding:3px 8px 3px 12px;border-radius:8px;z-index:40;pointer-events:auto;' +
      'background:linear-gradient(180deg,rgba(20,26,40,.94),rgba(8,12,24,.94));border:1.5px solid rgba(242,193,78,.55);box-shadow:0 3px 10px rgba(0,0,0,.5);' +
      'font:700 14px ' + FONT + ';color:#f4ecd8;', parent);
    r.dataset.testid = 'iap-offer';
    r.setAttribute('role', 'status');
    this.root = r;
    this.text = el('span', 'flex:1 1 auto;min-width:0;line-height:1.35;', r);
    const mk = (label: string, testid: string, gold: boolean, f: () => void): void => {
      const b = el('button', 'flex:0 0 auto;cursor:pointer;min-height:34px;padding:2px 12px;border-radius:8px;font:900 14px ' + FONT + ';' +
        (gold ? 'border:2px solid #fff1b8;background:linear-gradient(180deg,#ffe597,#e1a536);color:#2a1d05;' : 'border:1.5px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';'), r, label);
      b.type = 'button'; b.dataset.testid = testid;
      b.addEventListener('click', f);
    };
    mk('보기', 'iap-offer-view', true, () => this.open());
    mk('닫기', 'iap-offer-close', false, () => this.hide());
  }

  private readonly onEvent = (e: GameEvent): void => {
    if (e.type === Ev.LevelUp) { if (crossedMilestone(Math.round(e.a), Math.round(e.c)) !== null) this.pending ??= 'levelup'; }
    else if (e.type === Ev.JobChange) { if (e.c === 1) this.pending ??= 'job'; }
    else if (e.type === Ev.SceneChange) {
      if (e.a === Scene.Defeat) { if (this.streak.defeat(this.view.flow.stageId)) this.pending ??= 'defeat'; }
      else if (e.a === Scene.Result) this.streak.reset();
    }
  };

  private hide(): void { this.root.style.display = 'none'; this.shownTrigger = null; }

  private open(): void {
    this.hide();
    window.dispatchEvent(new CustomEvent(OPEN_IAP_TAB_EVENT));
    window.dispatchEvent(new CustomEvent(MENU_EVENT, { detail: 'shop' }));
  }

  update(now: number = Date.now()): void {
    this.cursor = this.view.events.readFrom(this.cursor, this.onEvent);
    const v = this.view, vv = v.village;
    const village = v.flow.scene === Scene.Village;
    // 마을을 떠나거나 창이 열리면 보이던 띠는 접는다(플레이 영역·창 위에 얹지 않는다)
    if (this.shownTrigger && (!village || vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen || v.flow.paused)) this.hide();
    if (!this.pending || this.shownTrigger || this.busy) return;
    // 전투 중이면 마을 복귀까지 미룬다. 창이 열려 있으면 닫힐 때까지
    if (!village || vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen || v.flow.paused) return;
    const trigger = this.pending;
    this.pending = null;
    const c = iapClient();
    // 결제 상점이 안 보이는 조건이면 제안도 안 한다
    const mode = c.mode();
    if ((mode !== 'web' && mode !== 'play') || !c.config) return;
    this.busy = true;
    // 구매 내역(이미 산 상품)을 받은 뒤에 고른다 — 산 스타터 팩을 다시 권하지 않게
    void c.refresh().then(() => this.finish(trigger, c, mode === 'play', now)).finally(() => { this.busy = false; });
  }

  private finish(trigger: OfferTrigger, c: ReturnType<typeof iapClient>, play: boolean, now: number): void {
    const v = this.view, vv = v.village;
    if (v.flow.scene !== Scene.Village || vv.menuNpc >= 0 || vv.inventoryOpen || vv.stageMapOpen || vv.questOpen || v.flow.paused || !c.config) return;
    const st = loadState();
    const today = kstDayKey(now);
    if (this.sessionDay === today || !canOffer(st, trigger, now)) return;
    const p = pickOfferProduct(c.config.products, c.bought, play);
    if (!p) return;
    saveState(markOffered(st, trigger, now));
    this.sessionDay = today;
    this.shownTrigger = trigger; this.shownProduct = p.id;
    this.text.textContent = offerText(trigger, p);
    this.root.style.display = 'flex';
    void c.sendEvent({ stage: 'offer', trigger, productId: p.id });
  }

  /** QA·시험이 읽는다 */
  get visible(): boolean { return this.shownTrigger !== null; }
  get product(): string { return this.shownProduct; }
}
